import { useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Snackbar from "@mui/material/Snackbar";
import Typography from "@mui/material/Typography";
import { getWebhookLogList } from "@/api/webhook-log/getWebhookLogList";
import type { GetWebhookLogListArgs } from "@/api/webhook-log/getWebhookLogList";
import { replayWebhookLog } from "@/api/webhook-log/replayWebhookLog";
import type { WebhookLog } from "@/api/webhook-log/_models/WebhookLog";
import { WebhookLogListFilters } from "./WebhookLogListFilters";
import { WebhookLogListHeader } from "./WebhookLogListHeader";
import { WebhookLogListRow } from "./WebhookLogListRow";

interface SnackState {
  open: boolean;
  severity: "success" | "error";
  message: string;
}

const toErrorText = (e: unknown): string => {
  const data = (e as { data?: { error?: unknown } })?.data;
  if (data && typeof data.error === "string") return data.error;
  return JSON.stringify(e);
};

/** Logged Cin7 / Shopify webhooks, with Replay per row; a replay shows up as a new row. */
export const WebhookLogList = () => {
  const [filters, setFilters] = useState<GetWebhookLogListArgs>({});
  const query = getWebhookLogList.useQuery(filters);
  const [replay] = replayWebhookLog.useMutation();
  const [replayingId, setReplayingId] = useState<string | null>(null);
  const [snack, setSnack] = useState<SnackState>({ open: false, severity: "success", message: "" });

  const handleReplay = async (webhookLog: WebhookLog) => {
    setReplayingId(webhookLog.id);
    try {
      const result = await replay(webhookLog.id).unwrap();

      let message = `Replayed ${webhookLog.event ?? "event"}: ${result.outcome}`;
      if (result.error) message = `${message} — ${result.error}`;
      setSnack({
        open: true,
        severity: result.outcome === "failed" ? "error" : "success",
        message,
      });
    }
    catch (e) {
      setSnack({ open: true, severity: "error", message: `Replay failed: ${toErrorText(e)}` });
    }
    finally {
      setReplayingId(null);
    }
  };

  const handleSnackClose = () => {
    setSnack({ ...snack, open: false });
  };

  const webhookLogs = query.data ?? [];

  return (
    <Box>
      <WebhookLogListFilters
        filters={filters}
        onChange={setFilters}
      />
      {query.isLoading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            p: 4,
          }}
        >
          <CircularProgress />
        </Box>
      )}
      {query.isError && (
        <Alert severity="error">
          Failed to load webhooks: {toErrorText(query.error)}
        </Alert>
      )}
      {query.isSuccess && (
        <Box sx={{ mx: -3 }}>
          <WebhookLogListHeader />
          {webhookLogs.length === 0 && (
            <Typography
              sx={{
                color: "text.secondary",
                p: 2,
              }}
              variant="body2"
            >
              No webhooks match.
            </Typography>
          )}
          {webhookLogs.map((webhookLog) => (
            <WebhookLogListRow
              isReplaying={replayingId === webhookLog.id}
              key={webhookLog.id}
              onReplay={handleReplay}
              webhookLog={webhookLog}
            />
          ))}
        </Box>
      )}
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        autoHideDuration={6000}
        onClose={handleSnackClose}
        open={snack.open}
      >
        <Alert
          onClose={handleSnackClose}
          severity={snack.severity}
          sx={{ width: "100%" }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
